import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { Grid, Button, TextField } from '@material-ui/core';
import { FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox } from '@material-ui/core';
import ImageIcon from '@material-ui/icons/ImageOutlined';
import AddBoxIcon from '@material-ui/icons/AddBox';
import Menu from '@material-ui/core/Menu';
import { useDispatch } from 'react-redux';

import { ArrowDb, updateArrow } from '../redux/arrowDbSlice';
import { generateImageId } from '../redux/imageDbSlice';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useOneArrowDb from '../hooks/useOneArrowDb';
import useLatelyImages from '../hooks/useLatelyImages';
import useElementDimensions from '../hooks/useElementDimensions';

import { deepCopy, FullSizeImageAddType, getImgSize, updateImageDbs } from '../general';
import Arrow from './Arrow';
import ImageBrowserDialog from './ImageBrowserDialog';
import DrawToolDialog from '../components/DrawToolDialog';
import ColorPalette from './ColorPalette';

const LabelTextField = styled(TextField)`
  width: 100%;
`
const HalfFormControl = styled(FormControl)`
  width: 48%;
  margin-right: 2%;
`
const HalfFormControlRight = styled(FormControl)`
  width: 48%;
  margin-left: 2%;
`
const NotesForm = styled.form`
  width: 100%;
`
const MenuButton = styled(Button)`
  size: 'large';
  width: 100%;
`

type EditArrowDialogProps = {
  open: boolean;
  onClose: () => void;
  id?: string;
  cutIn: (id: string) => void;
}
const EditArrowDialog: React.FC<EditArrowDialogProps> = ({ open, onClose, id, cutIn }) => {
  const dispatch = useDispatch();
  const [arrow, setArrow] = useState<ArrowDb | undefined>(undefined);
  const arrowValue = useOneArrowDb(id);
  const [imageAnchorEl, setImageAnchorEl] = useState<null | HTMLElement>(null);
  const userId = useCurrentUserId();
  const [imageBrowserAction, setImageBrowserAction] = useState({ show: false });
  const [drawToolAction, setDrawToolAction] = useState(false);
  const [latelyIds, , latelyImagesId] = useLatelyImages();
  const arrowPreviewWrapperId = 'edit-arrow-dialog-arrow-preview-wrapper-id';
  const arrowPreviewLength = useElementDimensions(arrowPreviewWrapperId, true).width;

  type keyObjType = { [key in string]: string };
  const lineStyles: keyObjType = {'solid': '実線', 'dotted': '点線', 'dashed':'破線', 'double':'二重線'}; 
  const shapes: keyObjType = {'arrow': '三角', 'circle': '丸', 'rectangle': '四角', 'rhombus': '菱形', 'flat': 'フラット', 'crow': 'カラスの足'};
  const line_thicknesss: keyObjType = {'verythin':'極細', 'thin':'細', 'medium':'通常', 'thick':'太', 'verythick':'極太'};
  const shape_sizes: keyObjType = {'small':'小', 'medium':'中', 'large':'大'}

  useEffect(() => setArrow(arrowValue), [arrowValue])

  if (!id || !arrow) return <></>;

  const changeArrowColor = (color: string) => {
    let newArrow = deepCopy(arrow);
    if(newArrow) newArrow.color = color;
    setArrow(newArrow);
  }
  const handleChangeThickness = (ev: any) => {
    let newArrow = deepCopy(arrow);
    if(newArrow) newArrow.thickness = ev.target.value;
    setArrow(newArrow);
  }
  const handleChangeLineStyle = (ev: any) => {
    let newArrow = deepCopy(arrow);
    if(newArrow) newArrow.lineStyle = ev.target.value;
    setArrow(newArrow);
  }
  const handleChangeReturn = (value: boolean) => {
    let newArrow = deepCopy(arrow);
    newArrow.return = value;
    setArrow(newArrow);
  }
  const handleChangeNotes = (ev: any) => {
    let newArrow = deepCopy(arrow);
    if(newArrow) newArrow.notes = ev.target.value;
    setArrow(newArrow);
  }
  const handleChangeLabels = (iMode: string, ev: any) => {
    let newArrow = deepCopy(arrow);
    const value = ev.target.value;
    if(newArrow){
      switch(iMode) {
        case 'head1' : newArrow.labels.head1.label = value; break;
        case 'head2' : newArrow.labels.head2.label = value; break;
        case 'middle1' : newArrow.labels.middle1.label = value; break;
        case 'middle2' : newArrow.labels.middle2.label = value; break;
        case 'tail1' : newArrow.labels.tail1.label = value; break;
        case 'tail2' : newArrow.labels.tail2.label = value; break;
      }
    } 
    setArrow(newArrow);
  }
  const handleChangeShapes = (iMode: string, ev: any) => {
    let newArrow = deepCopy(arrow);
    const value = ev.target.value;
    if(newArrow){ 
      switch(iMode) {
        case 'head' : newArrow.shapes.head.shape = value; break;
        case 'tail' : newArrow.shapes.tail.shape = value; break;
      }
    } 
    setArrow(newArrow);
  }
  const handleChangeShapeSizes = (iMode: string, ev: any) => {
    let newArrow = deepCopy(arrow);
    const value = ev.target.value;
    if(newArrow){
      switch(iMode) {
        case 'head' : newArrow.shapes.head.size = value; break;
        case 'tail' : newArrow.shapes.tail.size = value; break;
      }
    } 
    setArrow(newArrow);
  }
  const saveArrow = () => {
    if (id && arrow) dispatch(updateArrow({id: id, ...arrow}));
    onClose();
  }
  const imageHandleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setImageAnchorEl(event.currentTarget)
  }
  const imageHandleClose = () => {
    setImageAnchorEl(null)
  }
  const changeImage = (image: FullSizeImageAddType) => {
    let newArrow = deepCopy(arrow);
    if(newArrow){
      newArrow.imageId = image.id;
    }
    setArrow(newArrow);
    imageHandleClose();
  }
  const deleteImage = () => {
    let newArrow = deepCopy(arrow);
    if((newArrow) && (newArrow.imageId)){
      newArrow.imageId = '';
    } 
    setArrow(newArrow);
    imageHandleClose();
  }
  const openImageBrowser = () => {
    setImageBrowserAction({ show: true });
    imageHandleClose();
  }
  const openUserFileSelector = () => {
    imageHandleClose()
    const selector = document.getElementById('user-file-selector');
    if (!selector) return;
    selector.click();
  }
  const getFile = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (!ev.target.files) return;
    const file = ev.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result !== 'string') return;
      const dataUrlBase64 = reader.result;
      const [width, height] = getImgSize(dataUrlBase64);
      const imageId = generateImageId();
      updateImageDbs({ dispatch, imageId, dataUrlBase64, userId, latelyIds, latelyImagesId });
      changeImage({ id: imageId, width, height });
    }
  }
  const openDrawTool = () => {
    setDrawToolAction(true);
    imageHandleClose();
  }

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>矢印の詳細を設定します</DialogTitle>
      <DialogContent>
        <Grid container spacing={4}>
          <Grid container item xs={3} style={{padding: 32}}>
            <Grid container item xs={12}>
              <Grid item xs={7}>
                <FormControl variant="outlined" style={{width: '100%'}}>
                  <InputLabel id="arrow-thickness-select-label">太さ</InputLabel>
                  <Select
                    labelId="arrow-thickness-select-label"
                    label="太さ"
                    value={arrow?.thickness}
                    onChange={(ev) => handleChangeThickness(ev)}
                  >
                    { Object.keys(line_thicknesss).map((key, ix) => {
                      return <MenuItem value={key} key={ix}>{line_thicknesss[key]}</MenuItem>
                    }) }
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={1} />
              <Grid item xs={4} style={{marginTop: -4}}>
                <div style={{fontSize: '12px', color: '#616161'}}>
                  カラー
                </div>
                <ColorPalette
                  currentValue={arrow.color}
                  onChange={changeArrowColor}
                  labelDisplay={false}
                />
              </Grid>
            </Grid>
            <Grid container item xs={12}>
              <Grid item xs={7}>
                <FormControl variant="outlined" style={{width: '100%'}}>
                  <InputLabel id={'lineStyle_switch'}>線種</InputLabel>
                  <Select
                    labelId={'lineStyle_switch'} id={'lineStyle_switch'}
                    label="線種"
                    value={arrow?.lineStyle}
                    onChange={handleChangeLineStyle}
                  >
                    {Object.keys(lineStyles).map((key, ix) => {
                      return <MenuItem value={key} key={ix}>{lineStyles[key]}</MenuItem>
                    })}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container item xs={12}>
              <Grid item xs={1} />
              <Grid item xs={7}>
                <FormControlLabel label="Uターン" disabled={arrow.lineStyle === 'double'}
                  control={
                    <Checkbox checked={arrow.return} color="primary"
                      onChange={(ev) => handleChangeReturn(ev.currentTarget.checked)} />
                  } />
              </Grid>
            </Grid>
            <Grid container item xs={12}>
              <Grid item xs={12}>
                <Button color="primary" onClick={() => cutIn(id)}><AddBoxIcon fontSize="small" style={{ color: '#3e74fc', marginRight: 8 }} />カードカットインをする</Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid container item xs={9} spacing={2}>
            <Grid container item xs={12} spacing={2}>
              <Grid container item spacing={10}>
                <Grid item xs={4}>
                  <LabelTextField label="項目１" variant="outlined" value={arrow?.labels?.tail1.label} 
                              onChange={(ev) => handleChangeLabels('tail1', ev)}/>
                </Grid>
                <Grid item xs={4}>
                  <LabelTextField label="項目３" variant="outlined" value={arrow?.labels?.middle1.label} 
                              onChange={(ev) => handleChangeLabels('middle1', ev)}/>
                </Grid>
                <Grid item xs={4}>
                  <LabelTextField label="項目５" variant="outlined" value={arrow?.labels?.head1.label} 
                              onChange={(ev) => handleChangeLabels('head1', ev)}/>
                </Grid>
              </Grid>
              <Grid item xs={12} id={arrowPreviewWrapperId}>
                <Arrow id={id} data={arrow} sibLength={1} editPreview={true} editPreviewLength={arrowPreviewLength} />
              </Grid>
              <Grid container item spacing={10}>
                <Grid item xs={4}>
                  <LabelTextField label="項目２" variant="outlined" value={arrow?.labels?.tail2.label}
                            onChange={(ev) => handleChangeLabels('tail2', ev)} />
                </Grid>
                <Grid item xs={4}>
                  <LabelTextField label="項目４" variant="outlined" value={arrow?.labels?.middle2.label}
                            onChange={(ev) => handleChangeLabels('middle2', ev)} />
                </Grid>
                <Grid item xs={4}>
                  <LabelTextField label="項目６" variant="outlined" value={arrow?.labels?.head2.label} 
                            onChange={(ev) => handleChangeLabels('head2', ev)}/>
                </Grid>
              </Grid>
            </Grid>
            <Grid container item xs={12}>
              <Grid item xs={4}>
                <HalfFormControl variant="outlined">
                  <InputLabel id="from-shape-select-label">終端の形</InputLabel>
                  <Select
                    labelId="from-shape-select-label"
                    label="終端の形"
                    value={arrow?.shapes.tail.shape}
                    onChange={(ev) => handleChangeShapes('tail', ev)}
                  >
                    { Object.keys(shapes).map((key, ix) => {
                      return <MenuItem value={key} key={ix}>{shapes[key]}</MenuItem>
                    }) }
                  </Select>
                </HalfFormControl>
                <HalfFormControlRight variant="outlined">
                  <InputLabel id="from-size-select-label">大きさ</InputLabel>
                  <Select
                    labelId="from-size-select-label"
                    label="大きさ"
                    value={arrow?.shapes.tail.size}
                    onChange={(ev) => handleChangeShapeSizes('tail', ev)}
                  >
                    {Object.keys(shape_sizes).map((key, ix) => {
                      return <MenuItem value={key} key={ix}>{shape_sizes[key]}</MenuItem>
                    })}
                  </Select>
                </HalfFormControlRight>
              </Grid>
              <Grid container item xs={4}>
                <Grid item xs={3} />
                <Grid item xs={6}>
                  <MenuButton aria-controls="image-menu" aria-haspopup="true" color='primary' onClick={imageHandleClick}>
                    <ImageIcon /><pre> 画像</pre>
                  </MenuButton>
                  <Menu id="image-menu" anchorEl={imageAnchorEl} keepMounted
                    open={Boolean(imageAnchorEl)}
                    onClose={imageHandleClose}
                  >
                    <MenuItem onClick={openImageBrowser}>画像ブラウザ...</MenuItem>
                    <MenuItem onClick={openUserFileSelector} id="user-file-selector-button">
                      <input type="file" accept="image/png, image/jpeg" id="user-file-selector" onChange={getFile} style={{display: 'none'}} />ファイルから挿入...
                    </MenuItem>
                    <MenuItem onClick={openDrawTool}>絵を描いて挿入...</MenuItem>
                    <MenuItem onClick={deleteImage}>画像なし</MenuItem>
                  </Menu>
                </Grid>
                <Grid item xs={3} />
              </Grid>
              <Grid item xs={4}>
                <HalfFormControl variant="outlined">
                  <InputLabel id="to-shape-select-label">先端の形</InputLabel>
                  <Select
                    labelId="to-shape-select-label"
                    label="先端の形"
                    value={arrow?.shapes.head.shape}
                    onChange={(ev) => handleChangeShapes('head', ev)}
                  >
                    {Object.keys(shapes).map((key, ix) => {
                      return <MenuItem value={key} key={ix}>{shapes[key]}</MenuItem>
                    })} 
                  </Select>
                </HalfFormControl>
                <HalfFormControlRight variant="outlined">
                  <InputLabel id="to-size-select-label">大きさ</InputLabel>
                  <Select
                    labelId="to-size-select-label"
                    label="大きさ"
                    value={arrow?.shapes.head.size}
                    onChange={(ev) => handleChangeShapeSizes('head', ev)}
                  >
                    {Object.keys(shape_sizes).map((key, ix) => {
                      return <MenuItem value={key} key={ix}>{shape_sizes[key]}</MenuItem>
                    })}
                  </Select>
                </HalfFormControlRight>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <NotesForm autoComplete="off">
              <TextField label="備考テキスト欄" variant="outlined" value={arrow?.notes}
                        onChange={(ev) => handleChangeNotes(ev)} multiline rows={7} fullWidth />
            </NotesForm>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={onClose}>
          キャンセル
        </Button>
        <Button color='primary' onClick={saveArrow}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
    <ImageBrowserDialog
      open={imageBrowserAction.show}
      onAddAttribute={(kind, image) => {
        setImageBrowserAction({show: false});
        changeImage(image);
      }}
      onClose={() => setImageBrowserAction({show: false})} />
    <DrawToolDialog open={drawToolAction} onClose={() => setDrawToolAction(false)}
      onSave={(image: FullSizeImageAddType) => {
        setDrawToolAction(false);
        changeImage(image);
      }} />
    </>
  );
}

export default EditArrowDialog;
